import { onAuthStateChanged } from "firebase/auth";
import {
  auth,
  db,
  firebaseSignIn,
  firebaseSignInWithGoogle,
  firebaseSignOut,
  firebaseSignUp,
  forgotPassword,
  updateUserInfo,
  sendVerificationEmail,
  sendPasswordResetLink,
  uploadAvatar,
  firebaseDeleteUserAccount,
  updateUserAccessInfo,
} from "../firebase/authService";
import React, { createContext, useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import { getUserDeviceInfo } from "utils/deviceUtils";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(
    JSON.parse(localStorage.getItem("user")) || null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let timeoutId;
    if (error) {
      timeoutId = setTimeout(() => {
        setError(null);
      }, 5000);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [error]);

  // useEffect(() => {
  //   const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
  //     if (authUser) {
  //       try {
  //         const userDocRef = doc(db, "users", authUser.uid);
  //         const userDoc = await getDoc(userDocRef);

  //         if (userDoc.exists()) {
  //           const userData = userDoc.data();
  //           const token = await authUser.getIdToken();

  //           const userToStore = {
  //             uid: authUser.uid,
  //             firstName: userData.firstName,
  //             lastName: userData.lastName,
  //             email: authUser.email,
  //             emailVerified: authUser.emailVerified,
  //             avatar: userData.avatar,
  //             token,
  //           };
  //           if (!userToStore.emailVerified) {
  //             setUser(null);
  //           } else {
  //             setUser(userToStore);
  //             localStorage.setItem("user", JSON.stringify(userToStore));

  //             // Refresh the token periodically
  //             const refreshToken = async () => {
  //               const refreshedToken = await authUser.getIdToken(true); // Force refresh
  //               userToStore.token = refreshedToken;
  //               localStorage.setItem("user", JSON.stringify(userToStore));
  //             };

  //             const intervalId = setInterval(refreshToken, 1 * 60 * 1000); // Refresh every 15 minutes

  //             // Cleanup the interval on component unmount
  //             return () => clearInterval(intervalId);
  //           }
  //         }
  //       } catch (err) {
  //         console.error("Error fetching user data:", err);
  //       }
  //     } else {
  //       setUser(null);
  //       localStorage.removeItem("user");
  //     }
  //   });
  //   return () => unsubscribe();
  // }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        await fetchUserData(authUser);
      } else {
        clearUser();
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchUserData = async (authUser) => {
    try {
      const userDocRef = doc(db, "users", authUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        let token = await authUser.getIdToken();

        token = await refreshTokenIfExpired(authUser, token);

        // Capture device and browser info
        const userInfo = getUserDeviceInfo();

        const userToStore = {
          uid: authUser.uid,
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: authUser.email,
          emailVerified: authUser.emailVerified,
          avatar: userData.avatar,
          token,
          tokenExpiryTime: Date.now() + 3400 * 1000,
          trialStartDate: userData.trialStartDate,
          trialEndDate: userData.trialEndDate,
        };

        if (!userToStore.emailVerified) {
          clearUser();
        } else {
          setUser(userToStore);
          localStorage.setItem("user", JSON.stringify(userToStore));
          startTokenRefresh(authUser);
          await updateUserAccessInfo(authUser.uid, userInfo);
        }
      }
    } catch (err) {
      console.error("Error fetching user data:", err);
      setError("Failed to fetch user data.");
    } finally {
      setLoading(false);
    }
  };

  const refreshTokenIfExpired = async (authUser, currentToken) => {
    const tokenExpiryTime = getTokenExpiryTime();
    if (Date.now() > tokenExpiryTime) {
      const refreshedToken = await authUser.getIdToken(true);
      saveToken(refreshedToken);
      console.log("Return refresh token");
      return refreshedToken;
    }
    return currentToken;
  };

  const getTokenExpiryTime = () => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    return storedUser?.tokenExpiryTime || 0;
  };

  const saveToken = (token) => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (storedUser) {
      storedUser.token = token;
      storedUser.tokenExpiryTime = Date.now() + 3400 * 1000;
      localStorage.setItem("user", JSON.stringify(storedUser));
    }
  };

  const startTokenRefresh = (authUser) => {
    const refreshToken = async () => {
      const refreshedToken = await authUser.getIdToken();
      saveToken(refreshedToken);
    };

    const intervalId = setInterval(refreshToken, 50 * 60 * 1000); // Refresh every 50 minutes

    return () => clearInterval(intervalId);
  };

  const clearUser = () => {
    setUser(null);
    localStorage.removeItem("user");
  };

  const signUp = async (
    firstName,
    lastName,
    email,
    password,
    privacyPolicy,
    marketing
  ) => {
    setLoading(true);
    try {
      await firebaseSignUp(
        firstName,
        lastName,
        email,
        password,
        privacyPolicy,
        marketing
      );
    } catch (err) {
      toast.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const user = await firebaseSignIn(email, password);
      if (!user.emailVerified) {
        setUser(null);
      } else {
        setUser(user);
      }
      setError(null);
      return user;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async (
    privacyPolicy,
    marketing,
    isSignup = false
  ) => {
    setLoading(true);
    try {
      const user = await firebaseSignInWithGoogle(privacyPolicy, marketing);
      setUser(user);
      setError(null);
    } catch (err) {
      if (isSignup) {
        toast.error(err);
      } else {
        setError(err);
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      await firebaseSignOut();
      setUser(null);
      setError(null);
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const resendEmailVerifyToken = async () => {
    setLoading(true);
    try {
      await sendVerificationEmail();
      /// hover to fetch user
      setError(null);
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email) => {
    setLoading(true);
    try {
      await forgotPassword(email);
      setError(null);
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (firstName, lastName) => {
    setLoading(true);
    try {
      await updateUserInfo(firstName, lastName);
      setError(null);
      setUser((prevUser) => ({ ...prevUser, firstName, lastName }));
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const changeAvatar = async (file) => {
    setLoading(true);
    setError(null);
    try {
      const fileUrl = await uploadAvatar(file);
      const updatedUser = { ...user, avatar: fileUrl };

      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
    } catch (err) {
      toast.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async (email) => {
    setLoading(true);
    try {
      await sendPasswordResetLink(email);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const deleteUserAccount = async () => {
    try {
      await firebaseDeleteUserAccount();
    } catch (err) {
      console.log("Delete user error", err);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        fetchUserData,
        signUp,
        signIn,
        signInWithGoogle,
        signOut,
        resendEmailVerifyToken,
        resetPassword,
        updateUser,
        updatePassword,
        changeAvatar,
        deleteUserAccount,
        loading,
        error,
        setLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
